<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { ElNotification } from 'element-plus'

// Components
import BreadCrumbNavigationTemplate from 'Common/components/templates/BreadCrumbNavigationTemplate.vue'
import TitledPageTemplate from 'Common/components/templates/TitledPageTemplate.vue'
// Modules
import ReportSummaryHeader from 'Modules/pages/reportDashboard/ReportSummaryHeader.vue'
import ReportSummarySidebar from 'Modules/pages/reportDashboard/ReportSummarySidebar.vue'
import ReportSummaryContent from 'Modules/pages/reportDashboard/ReportSummaryContent.vue'
import ReportSummaryHeaderActions from 'Modules/pages/reportDashboard/ReportSummaryHeaderActions.vue'
// Utils
import { summarizeDefects } from 'Modules/pages/reportDashboard/utils/summaryGenerator'
//API
import { useSummaryDataQuery } from 'API/queries/iris/report-summary'
import { useGetAirCraftsFiltered } from 'API/queries/iris/aircraft'
import { useGetInspectionInstancesFiltered } from 'API/queries/iris/inspection-instance'
//Store
import { useFilterStore } from 'Store/filterStore'
// Types
import type { BreadCrumbItem } from 'Common/types/utils/breadCrumbItem'
import type { ImagesType } from 'Modules/types/generic/image/ImagesType'
import { ImageReviewStates } from 'Modules/types/generic/image/ImageReviewStates'
import { RequestStatus } from 'API/enums'
import { ImageDetailType } from 'Modules/types/generic/image/imageDetailType'
import { getCookie, setCookie } from '@/common/utils/cookie'

// Page
const route = useRoute()
const layout = computed(() => defineAsyncComponent(() => import(`Modules/pages/reportDashboard/PageLayout.vue`)))
const breadCrumbs = computed((): Array<BreadCrumbItem> => {
  return [
    { label: 'Home', route: 'Home' },
    { label: 'Summary Report', route: 'SummaryReport' },
  ]
})

//State
const filterStore = useFilterStore()
const { queryParamObj, areFiltersReady } = storeToRefs(filterStore)

const aircraftParam = computed<string | string[] | undefined>(() => {
  return queryParamObj.value?.aircraft
})

// Data
const {
  data: summaryData,
  status: summaryStatus,
  isLoading: isSummaryDataLoading,
} = useSummaryDataQuery({
  variables: { filterQueryParams: queryParamObj },
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const { data: aircraftData, isLoading: isAircraftDataLoading } = useGetAirCraftsFiltered({
  variables: {
    filterQueryParams: {
      aircraft: aircraftParam,
    },
  },
  staleTime: 300000,
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const { data: inspectionData, isLoading: isInspectionDataLoading } = useGetInspectionInstancesFiltered({
  variables: {
    filterQueryParams: {
      aircraft: aircraftParam,
    },
  },
  staleTime: 300000,
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const summaryImageDetails = computed<ImagesType>(() => {
  if (summaryStatus.value !== RequestStatus.success) return {}

  // separate images by complete / incomplete
  return summaryData.value.images.reduce(
    (acc: ImagesType, i: ImageDetailType) => {
      if (i.reviewState === ImageReviewStates.Complete) {
        acc.complete.push(i)
      } else {
        acc.incomplete.push(i)
      }
      return acc
    },
    { incomplete: [], complete: [] }
  )
})

const measurementsSummaryData = computed<Array<ImagesType>>(() => {
  if (summaryStatus.value !== RequestStatus.success) return []
  return summarizeDefects(summaryData.value.annotations)
})

const summaryAnnotationDetails = computed<ImagesType>(() => {
  if (summaryStatus.value !== RequestStatus.success) return []
  return summaryData.value.annotations
})

const EXP_MEASUREMENT_ACKNOWLEDGEMENT = 'EXP_MEASUREMENT_ACKNOWLEDGEMENT'
const notifyMessage = ref()
onMounted(() => {
  const userAcknowledgement = getCookie(EXP_MEASUREMENT_ACKNOWLEDGEMENT)

  if (!userAcknowledgement) {
    notifyMessage.value = ElNotification({
      title: 'Experimental Feature',
      message:
        'The measurements displayed are part of an experimental feature. While we strive for accuracy, the values shown may not be 100% precise. Please consider this when using these measurements for your work.',
      duration: 0,
      type: 'info',
      position: 'bottom-left',
      onClose: () => {
        // since we close on Unmounted, lets detect a route change to prevent user acknowledgment
        if (route.name === 'SummaryReport') {
          setCookie(EXP_MEASUREMENT_ACKNOWLEDGEMENT, true, 1)
        }
      },
    })
  }
})

onUnmounted(() => {
  if (notifyMessage.value) {
    notifyMessage.value.close()
    notifyMessage.value = null
  }
})
</script>

<template>
  <BreadCrumbNavigationTemplate :items="breadCrumbs">
    <template #main-content>
      <TitledPageTemplate title="Summary Report">
        <template #main-content>
          <component :is="layout">
            <template #summary-header-actions><ReportSummaryHeaderActions /></template>
            <template #summary-overview>
              <ReportSummaryHeader
                :measurements-summary-data="measurementsSummaryData"
                :measurement-data-loading="isSummaryDataLoading"
                :annotations="summaryAnnotationDetails"
                :annotations-loading="isSummaryDataLoading"
                :aircraft="aircraftData"
                :aircraft-loading="isAircraftDataLoading"
              />
            </template>
            <template #summary-side-bar>
              <ReportSummarySidebar
                :aircraft-data="aircraftData"
                :aircraft-loading="isAircraftDataLoading"
                :inspection-data="inspectionData"
                :inspection-data-loading="isInspectionDataLoading"
                :annotation-data="summaryAnnotationDetails"
                :annotations-loading="isSummaryDataLoading"
              />
            </template>
            <template #summary-content>
              <ReportSummaryContent
                :images="summaryImageDetails"
                :annotations="summaryAnnotationDetails"
                :loading="isSummaryDataLoading"
              />
            </template>
          </component>
        </template>
      </TitledPageTemplate>
    </template>
  </BreadCrumbNavigationTemplate>
</template>
